import MaterialReactTable from "material-react-table";
import { useEffect, useRef, useState } from "react";
import {
  EmptyPaper,
  LoadingError,
  LoadingPaper,
} from "../../LoadingComponents";
import domtoimage from "dom-to-image";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility as ViewIcon } from "@mui/icons-material";
import { pressureColumns, commonColumns } from "./TabsColumns";
import { useGetPatientReports } from "../useReportQueries";
import PressureReport from "../ViewReport/PressureReport";
import CanvasJS from "@canvasjs/charts";
import ReactPDF, { PDFDownloadLink, usePDF } from "@react-pdf/renderer";
import getReportPdf, {
  ReportPdfFull,
  ReportPdfMixed,
  ReportPdfSingle,
} from "../ViewReport/ReportPdf";
import MixedReport from "../ViewReport/MixedReport";
import { useGetPatientById } from "../../PatientComponents";
import { downloadFile, renderEUDate, todayDate } from "../../../utils/utils";
import dayjs from "dayjs";
import ButtonDatePicker from "../../ButtonDatePicker/ButtonDatePicker";
import { generatePressureChart } from "../ViewReport/reportUtils/pressureReport";
import { generateSPO2Chart } from "../ViewReport/reportUtils/spo2Report";
import { generateTemperatureChart } from "../ViewReport/reportUtils/temperatureReport";
import {
  generateMixedChart,
  generateMixedChartSmall,
} from "../ViewReport/reportUtils/utils";
import { useGetOrganizationById } from "../../HandlingComponents";
import { useAuthUser } from "react-auth-kit";
import { generateHRChart } from "../ViewReport/reportUtils/hrReport";

function SummaryTab({ patient_id, org_id }) {
  const getSpo2Reports = useGetPatientReports(patient_id, 1);
  const getPressureReports = useGetPatientReports(patient_id, 3);
  const getTemperatureReports = useGetPatientReports(patient_id, 4);
  const authUser = useAuthUser();
  const _org_id = authUser().user.org_id ? authUser().user.org_id : org_id;
  const organizationQuery = useGetOrganizationById(_org_id);

  const canvas_spo2_ref = useRef(null);
  const canvas_hr_ref = useRef(null);
  const canvas_pressure_ref = useRef(null);
  const canvas_mixed_ref = useRef(null);
  const canvas_temperature_ref = useRef(null);

  const blankFullReport = {
    patientInfo: {
      first_name: "",
      last_name: "",
      date_of_birth: "",
      startDate: "",
      endDate: "",
      doctor: {
        first_name: "",
        last_name: "",
      },
    },
    chartSpo2: null,
    chartPressure: null,
    chartHR: null,
    chartTemperature: null,
    chartOmnia: null,
  };

  // set logo info
  const [logoData, setLogoData] = useState({
    includeH3K: true,
    logo: "",
    clientLogoPos: "DX",
  });
  const [orgData, setOrgData] = useState({
    org_name: "",
    address: "",
    cap: "",
    city: "",
  });

  useEffect(() => {
    if (organizationQuery.isSuccess && organizationQuery.data != null) {
      setLogoData({
        clientLogoPos: organizationQuery.data.client_logo_pos,
        logo: organizationQuery.data.logo,
        includeH3K: organizationQuery.data.include_h3k_logo,
      });
      setOrgData({
        org_name: organizationQuery.data.name,
        address: organizationQuery.data.address,
        cap: organizationQuery.data.cap,
        city: organizationQuery.data.city,
      });
    }
  }, [organizationQuery.status]);

  const fullReportInfo = useRef(blankFullReport);
  const generatingReportsFull = useRef(false);

  const FullDocument = (
    <ReportPdfFull
      logoInfo={logoData}
      orgInfo={orgData}
      patientInfo={fullReportInfo.current.patientInfo}
      chart_omnia={fullReportInfo.current.chartOmnia}
      chart_temperature={fullReportInfo.current.chartTemperature}
      chart_spo2={fullReportInfo.current.chartSpo2}
      chart_pressure={fullReportInfo.current.chartPressure}
      chart_HR={fullReportInfo.current.chartHR}
    />
  );

  const [instanceFull, updateFull] = usePDF({ document: FullDocument });

  const [userData, setUserData] = useState(null);

  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());

  const updateStartDate = (date) => {
    // set time to 00:00:00
    const _date = date.set("hour", 0).set("minute", 0).set("second", 0);
    setStartDate(_date);

    fullReportInfo.current = blankFullReport;
    generatingReportsFull.current = false;
  };

  const updateEndDate = (date) => {
    // set time to 23:59:59
    const _date = date.set("hour", 23).set("minute", 59).set("second", 59);
    setEndDate(_date);

    fullReportInfo.current = blankFullReport;
    generatingReportsFull.current = false;
  };

  // get patient data
  const patientQuery = useGetPatientById(patient_id);
  useEffect(() => {
    if (patientQuery.isSuccess) {
      setUserData(patientQuery.data);
    }
  }, [patientQuery.status]);

  const downloadFull = () => {
    downloadFile({
      data: instanceFull.blob,
      fileName: `report_${userData.last_name}_${userData.first_name}.pdf`,
      fileType: "application/pdf",
    });
  };

  const restrictReportsDateRange = (_startDate, _endDate) => {
    // restrict data to the selected date range
    const filteredSpo2Reports = getSpo2Reports.data.reports.filter(
      (report) =>
        new Date(report.date) >= new Date(_startDate) &&
        new Date(report.date) <= new Date(_endDate)
    );

    const filteredPressureReports = getPressureReports.data.reports.filter(
      (report) =>
        new Date(report.date) >= new Date(_startDate) &&
        new Date(report.date) <= new Date(_endDate)
    );

    const filteredHRReports = getPressureReports.data.reports.filter(
      (report) =>
        new Date(report.date) >= new Date(_startDate) &&
        new Date(report.date) <= new Date(_endDate)
    );
    filteredHRReports.filter(
      (report) => report.data.HR != null && report.data.HR != undefined
    );

    const filteredTemperatureReports =
      getTemperatureReports.data.reports.filter(
        (report) =>
          new Date(report.date) >= new Date(_startDate) &&
          new Date(report.date) <= new Date(_endDate)
      );

    return {
      spo2Reports: filteredSpo2Reports,
      pressureReports: filteredPressureReports,
      hrReports: filteredHRReports,
      temperatureReports: filteredTemperatureReports,
    };
  };

  const generateFullReport = () => {
    generatingReportsFull.current = true;
    try {
      // get reports layouts
      const { spo2Reports, pressureReports, temperatureReports, hrReports } =
        restrictReportsDateRange(startDate, endDate);

      const pressureLayout = generatePressureChart(
        pressureReports,
        true, // small
        false // includeHR
      );
      const spo2Layout = generateSPO2Chart(spo2Reports, true, false);
      const temperatureLayout = generateTemperatureChart(
        temperatureReports,
        true
      );
      const HRLayout = generateHRChart(hrReports, true);
      const omniaLayout = generateMixedChart(
        pressureReports,
        spo2Reports,
        temperatureReports,
        true
      );

      canvas_spo2_ref.current.width = 1200;
      canvas_spo2_ref.current.height = 375;

      // genero i report
      const chart_spo2 = new CanvasJS.Chart(canvas_spo2_ref.current, {
        ...spo2Layout,
      });
      const chart_pressure = new CanvasJS.Chart("canvas_pressure", {
        ...pressureLayout,
      });
      const chart_hr = new CanvasJS.Chart("canvas_hr", {
        ...HRLayout,
      });
      const chart_temperature = new CanvasJS.Chart("canvas_temperature", {
        ...temperatureLayout,
      });
      const chart_omnia = new CanvasJS.Chart("canvas_mixed", {
        ...omniaLayout,
      });

      // render dei report nascosto
      chart_spo2.render();
      chart_pressure.render();
      chart_hr.render();
      chart_temperature.render();
      chart_omnia.render();

      // generazione del documento
      fullReportInfo.current.patientInfo = {
        ...fullReportInfo.current.patientInfo,
        ...userData,
        startDate: renderEUDate(startDate),
        endDate: renderEUDate(endDate),
      };
      fullReportInfo.current.logoInfo = {
        ...fullReportInfo.current.logoInfo,
        ...logoData,
      };

      // check if they're empty
      fullReportInfo.current.chartPressure =
        pressureReports.length === 0 ? null : chart_pressure.canvas.toDataURL();
      fullReportInfo.current.chartHR =
        hrReports.length === 0 ? null : chart_hr.canvas.toDataURL();
      fullReportInfo.current.chartSpo2 =
        spo2Reports.length === 0 ? null : chart_spo2.canvas.toDataURL();
      fullReportInfo.current.chartTemperature =
        temperatureReports.length === 0
          ? null
          : chart_temperature.canvas.toDataURL();
      fullReportInfo.current.chartOmnia =
        spo2Reports.length === 0 &&
        pressureReports.length === 0 &&
        temperatureReports.length === 0
          ? null
          : chart_omnia.canvas.toDataURL();

      generatingReportsFull.current = false;
      updateFull(
        <ReportPdfFull
          logoInfo={logoData}
          orgInfo={orgData}
          patientInfo={fullReportInfo.current.patientInfo}
          chart_temperature={fullReportInfo.current.chartTemperature}
          chart_spo2={fullReportInfo.current.chartSpo2}
          chart_HR={fullReportInfo.current.chartHR}
          chart_pressure={fullReportInfo.current.chartPressure}
          chart_omnia={fullReportInfo.current.chartOmnia}
        />
      );
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <main
      style={{
        width: "100%",
      }}
    >
      {getTemperatureReports.isLoading ||
      getPressureReports.isLoading ||
      getSpo2Reports.isLoading ? (
        <LoadingPaper />
      ) : getTemperatureReports.isError ||
        getPressureReports.isError ||
        getSpo2Reports.isError ? (
        <LoadingError error={"Errore nel caricamento dei report"} />
      ) : (
        getTemperatureReports.isSuccess &&
        getPressureReports.isSuccess &&
        getSpo2Reports.isSuccess &&
        (getTemperatureReports.data.reports.length === 0 &&
        getPressureReports.data.reports.length === 0 &&
        getSpo2Reports.data.reports.length === 0 ? (
          <EmptyPaper message={"Nessun report associato al paziente"} />
        ) : (
          <Box>
            <Stack direction="row" spacing={2}>
              {fullReportInfo.current.patientInfo.first_name == "" ? (
                generatingReportsFull.current ? (
                  <CircularProgress />
                ) : (
                  <Button onClick={generateFullReport}>Genera report</Button>
                )
              ) : (
                <Button
                  color="error"
                  disabled={instanceFull.loading}
                  onClick={downloadFull}
                >
                  Download report
                </Button>
              )}

              {/* {singleReportInfo.current.patientInfo.first_name == "" ? (
                generatingReportsSingle.current ? (
                  <CircularProgress />
                ) : (
                  <Button onClick={generateSingleReport}>
                    Genera report singoli
                  </Button>
                )
              ) : (
                <Button
                  color="error"
                  disabled={instanceSingle.loading}
                  onClick={downloadSingle}
                >
                  Download report singoli
                </Button>
              )}

              {mixedReportInfo.current.patientInfo.first_name == "" ? (
                generatingReportsMixed.current ? (
                  <CircularProgress />
                ) : (
                  <Button onClick={generateMixedReport}>
                    Genera report mixed
                  </Button>
                )
              ) : (
                <Button
                  color="error"
                  disabled={instanceMixed.loading}
                  onClick={downloadMixed}
                >
                  Download report mixed
                </Button>
              )} */}
            </Stack>
            <canvas
              style={{ display: "none" }}
              ref={canvas_spo2_ref}
              id="canvas_spo2"
            ></canvas>
            <canvas
              style={{ display: "none" }}
              ref={canvas_pressure_ref}
              id="canvas_pressure"
            ></canvas>
            <canvas
              style={{ display: "none" }}
              ref={canvas_mixed_ref}
              id="canvas_mixed"
            ></canvas>
            <canvas
              style={{ display: "none" }}
              ref={canvas_temperature_ref}
              id="canvas_temperature"
            ></canvas>
            <canvas
              style={{ display: "none" }}
              ref={canvas_hr_ref}
              id="canvas_hr"
            ></canvas>
            <>
              <Box
                sx={{
                  padding: 2,
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" gutterBottom>
                  Seleziona intervallo di date
                </Typography>
                <Stack direction="row" spacing={2}>
                  <ButtonDatePicker
                    id="startDate"
                    label="Start Date"
                    value={startDate}
                    onChange={(date) => updateStartDate(date)}
                  />
                  <ButtonDatePicker
                    id="endDate"
                    label="End Date"
                    value={endDate}
                    onChange={(date) => updateEndDate(date)}
                  />
                </Stack>
              </Box>
              <MixedReport
                patient_id={patient_id}
                startDate={startDate}
                endDate={endDate}
              />
            </>
          </Box>
        ))
      )}
    </main>
  );
}

export default SummaryTab;
